import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Plus } from '@digital-twins/design-system/icons'

import { Button } from '@/components/ui/button'
import { AddFilterModal } from '@/layouts/AudienceImportSessionLayout/components/AddFilterModal'
import { FilterChipGrid } from '@/layouts/AudienceImportSessionLayout/components/FilterChipGrid'
import { FilterDetailModal } from '@/layouts/AudienceImportSessionLayout/components/FilterDetailModal'
import {
  resolveActiveFilterColumns,
  usePreviewFilterSelection,
} from '@/layouts/AudienceImportSessionLayout/hooks/usePreviewFilterSelection'
import { cn } from '@/lib/utils'

import { AudienceSummary } from '@/lib/api'
import {
  AudienceImportPreviewProjection,
  AudienceImportSegmentFiltersColumnGroup,
} from '@digital-twins/shared-types'
import {
  FilterChipGroup,
  FilterChipItem,
} from '@/types/components/audiences/filter-chip-grid'
import type { ColumnGroupMetadata } from '@/types/routes/tests'
import type { SegmentFilterQuestion } from '@/lib/utils/segment-filter-questions'

export interface AudienceFilterColumnMeta {
  name: string
  displayLabel?: string
  /** User-defined alias for the question group this column belongs to. */
  groupDisplayLabel?: string
  canonicalId?: string | null
  category?: string
  uniqueValues?: Array<string>
  valueLabels?: Record<string, string>
  isOperational?: boolean
}

export interface AudienceFilterEditorState {
  filterChipItems: Array<FilterChipItem>
  filterQuestions: Array<SegmentFilterQuestion>
  /** Full filterable question universe (singles + groups), regardless of active state. */
  allQuestions: Array<SegmentFilterQuestion>
  filterGroups: Array<FilterChipGroup>
  activeFilterColumns: Array<string>
  initialResolvedFilterColumns: Array<string>
  hasChanges: boolean
  addFilter: (sourceColumn: string, customLabel?: string) => void
  removeFilter: (sourceColumn: string) => void
  renameFilter: (sourceColumn: string, newLabel: string) => void
  removeGroup: (groupId: string) => void
  renameGroup: (groupId: string, newLabel: string) => void
  addQuestion: (question: SegmentFilterQuestion, customLabel?: string) => void
}

interface UseAudienceFilterEditorArgs {
  audience: AudienceSummary | null
  columns: Array<AudienceFilterColumnMeta>
  groups?: Array<ColumnGroupMetadata>
  selectedColumns: Array<string> | null
  onSelectedColumnsChange: (columns: Array<string> | null) => void
  customLabels: Record<string, string>
  onCustomLabelsChange: (labels: Record<string, string>) => void
}

interface AudienceFilterEditorPanelProps {
  editor: AudienceFilterEditorState
  className?: string
}

export function createAudienceFilterProjection(
  audience: AudienceSummary,
  columns: Array<AudienceFilterColumnMeta>,
  groups: Array<ColumnGroupMetadata> = [],
): AudienceImportPreviewProjection {
  const mappingsBySource = new Map(
    (audience.segmentationMappings ?? []).map((mapping) => [
      mapping.sourceColumn,
      mapping,
    ]),
  )
  const columnsBySource = new Map(columns.map((column) => [column.name, column]))

  // Reshape the authoritative effective groups (from GET /populations/:id/columns,
  // already remapped to canonical source columns and carrying any user alias on the
  // members' `groupDisplayLabel`) into the preflight `columnGroups` shape the shared
  // question builder consumes. Without this the editor falls back to the label-stem
  // heuristic, which can't group code-style names (e.g. `brand_awareness_r1`).
  const columnGroups: Array<AudienceImportSegmentFiltersColumnGroup> = groups
    .filter((group) => group.memberColumns.length >= 2)
    .map((group) => {
      const aliasMember = group.memberColumns
        .map((name) => columnsBySource.get(name)?.groupDisplayLabel?.trim())
        .find((alias) => Boolean(alias))
      return {
        groupId: group.groupId,
        columns: group.memberColumns,
        groupType: group.groupType,
        parentLabel: aliasMember ?? group.rawLabel ?? null,
        detectionRoute: group.detectionRoute ?? null,
      }
    })
  const allSourceColumns = new Set<string>([
    ...Array.from(mappingsBySource.keys()),
    ...columns.map((column) => column.name),
  ])

  const filterColumnSamples = columns.reduce<Record<string, Array<string>>>(
    (acc, column) => {
      const vals = column.uniqueValues?.slice(0, 10) ?? []
      if (vals.length > 0) {
        acc[column.name] = vals
      } else {
        acc[column.name] = column.valueLabels ? Object.keys(column.valueLabels) : []
      }
      return acc
    },
    {},
  )

  const segmentationMappings = Array.from(allSourceColumns).map((sourceColumn) => {
    const mapping = mappingsBySource.get(sourceColumn)
    const column = columnsBySource.get(sourceColumn)
    if (mapping) {
      return {
        ...mapping,
        displayLabel: mapping.displayLabel ?? column?.displayLabel ?? sourceColumn,
        valueLabels: mapping.valueLabels ?? column?.valueLabels,
      }
    }
    return {
      sourceColumn,
      canonicalId: column?.canonicalId ?? null,
      displayLabel: column?.displayLabel ?? sourceColumn,
      mappingSource: 'manual' as const,
      valueLabels: column?.valueLabels,
      isOperational: column?.isOperational ?? false,
    }
  })

  return {
    sessionId: `audience-${audience.id}`,
    machineVersion: 0,
    updatedAt: new Date().toISOString(),
    expiresAt: null,
    audienceDraft: {
      name: audience.name ?? 'Audience',
      audienceType: audience.audienceType ?? null,
    },
    respondentCount: audience.rowCount ?? 0,
    fileSummary: null,
    sheetAssignment: null,
    requiredFields: null,
    analysis: null,
    preview: null,
    contextMaterials: null,
    warnings: [],
    flowSnapshot: {} as AudienceImportPreviewProjection['flowSnapshot'],
    segmentationMappings,
    constantContext: audience.constantContext ?? {},
    filterColumnSamples,
    segmentFiltersPreflight: {
      filterColumnSamples,
      columnGroups,
      operationalColumns: columns
        .filter((column) => column.isOperational)
        .map((column) => column.name),
    },
    segmentFilterCandidates: segmentationMappings.map((mapping) => ({
      sourceColumn: mapping.sourceColumn,
      displayLabel: mapping.displayLabel ?? mapping.sourceColumn,
      originalLabel: mapping.displayLabel ?? mapping.sourceColumn,
      canonicalId: mapping.canonicalId ?? null,
      mappingSource: 'manual',
      sampleValues: filterColumnSamples[mapping.sourceColumn] ?? [],
    })),
  } as unknown as AudienceImportPreviewProjection
}

export function getLockedFilterRemovalMessage(editor: AudienceFilterEditorState): string | null {
  const initiallyLocked = new Set(editor.initialResolvedFilterColumns)
  const lockedMissing = editor.filterChipItems.find(
    (item) =>
      item.isLocked &&
      initiallyLocked.has(item.sourceColumn) &&
      !editor.activeFilterColumns.includes(item.sourceColumn),
  )

  if (!lockedMissing) {
    return null
  }

  return `${lockedMissing.displayLabel} is required and cannot be removed.`
}

export function useAudienceFilterEditor({
  audience,
  columns,
  groups,
  selectedColumns,
  onSelectedColumnsChange,
  customLabels,
  onCustomLabelsChange,
}: UseAudienceFilterEditorArgs): AudienceFilterEditorState {
  const projection = useMemo(() => {
    if (!audience) return null
    return createAudienceFilterProjection(audience, columns, groups)
  }, [audience, columns, groups])

  const initialSelectedColumns = useMemo(() => {
    if (!audience || !Array.isArray(audience.activeFilterColumns)) return null
    return audience.activeFilterColumns
  }, [audience])

  const {
    filterChipItems,
    filterQuestions,
    allQuestions,
    filterGroups,
    activeFilterColumns,
    addFilter,
    removeFilter,
    renameFilter,
    removeGroup,
    renameGroup,
    addQuestion,
  } = usePreviewFilterSelection(projection, {
    persistedSelection: selectedColumns,
    onSelectionChange: onSelectedColumnsChange,
    persistedLabels: customLabels,
    onLabelsChange: onCustomLabelsChange,
  })

  const initialResolvedFilterColumns = useMemo(
    () => resolveActiveFilterColumns(initialSelectedColumns, filterChipItems),
    [filterChipItems, initialSelectedColumns],
  )

  const hasChanges = useMemo(() => {
    if (Object.keys(customLabels).length > 0) return true
    if (activeFilterColumns.length !== initialResolvedFilterColumns.length) return true
    return activeFilterColumns.some(
      (column, index) => column !== initialResolvedFilterColumns[index],
    )
  }, [activeFilterColumns, initialResolvedFilterColumns, customLabels])

  return {
    filterChipItems,
    filterQuestions,
    allQuestions,
    filterGroups,
    activeFilterColumns,
    initialResolvedFilterColumns,
    hasChanges,
    addFilter,
    removeFilter,
    renameFilter,
    removeGroup,
    renameGroup,
    addQuestion,
  }
}

export function AudienceFilterEditorPanel({
  editor,
  className,
}: AudienceFilterEditorPanelProps) {
  const { t } = useTranslation('app')
  const [isAddModalOpen, setIsAddModalOpen] = useState(false)
  const [selectedDetailItem, setSelectedDetailItem] = useState<FilterChipItem | null>(null)
  const [selectedDetailGroup, setSelectedDetailGroup] = useState<FilterChipGroup | null>(null)

  return (
    <section className={cn('rounded-3xl border border-border bg-card p-5 shadow-sm sm:p-7', className)}>
      <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-lg font-semibold text-card-foreground">{t('audienceActions.activeFilters')}</h2>
          <p className="text-sm text-muted-foreground">
            {editor.activeFilterColumns.length} filters selected from {editor.filterChipItems.length} available columns.
          </p>
        </div>
        <Button variant="outline" className="gap-2" onClick={() => setIsAddModalOpen(true)}>
          <Plus className="h-4 w-4" />
          {t('audienceActions.addFilter')}
        </Button>
      </div>

      <FilterChipGrid
        items={editor.filterChipItems}
        selected={editor.activeFilterColumns}
        onRemove={editor.removeFilter}
        onChipClick={setSelectedDetailItem}
        onAddClick={() => setIsAddModalOpen(true)}
        groups={editor.filterGroups}
        onRemoveGroup={editor.removeGroup}
        onGroupClick={setSelectedDetailGroup}
      />

      <AddFilterModal
        open={isAddModalOpen}
        onOpenChange={setIsAddModalOpen}
        questions={editor.filterQuestions}
        onAdd={editor.addQuestion}
      />

      {(selectedDetailItem || selectedDetailGroup) && (
        <FilterDetailModal
          open={Boolean(selectedDetailItem || selectedDetailGroup)}
          onOpenChange={(open) => {
            if (!open) {
              setSelectedDetailItem(null)
              setSelectedDetailGroup(null)
            }
          }}
          item={selectedDetailItem}
          sampleValues={selectedDetailItem?.sampleValues ?? []}
          onRename={(sourceColumn, newLabel) => {
            editor.renameFilter(sourceColumn, newLabel)
            setSelectedDetailItem((prev) =>
              prev ? { ...prev, displayLabel: newLabel } : prev,
            )
          }}
          group={selectedDetailGroup}
          onRenameGroup={(groupId, newLabel) => {
            editor.renameGroup(groupId, newLabel)
            setSelectedDetailGroup((prev) =>
              prev ? { ...prev, label: newLabel } : prev,
            )
          }}
        />
      )}
    </section>
  )
}
